import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import { Box, SxProps } from "@mui/material";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Button, { ButtonVariant } from "../utils/Button";

export type SelectionListProps = {
  itemsLength: number;
  minLengthForControls: number;
  children: ReactNode;
  scrollSize: number;
  sx?: SxProps;
};

const SelectionList = ({
  children,
  itemsLength,
  minLengthForControls,
  scrollSize,
  sx = {}
}: SelectionListProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [isMouseHolding, setIsMouseHolding] = useState(false);
  const [direction, setDirection] = useState<"left" | "right">("right");

  const { t } = useTranslation();

  const stop = () => {
    clearInterval(timerRef.current);
    setIsMouseHolding(false);
  };

  const scrollItems = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) {
      el.scrollLeft =
        direction === "left" ? el.scrollLeft - scrollSize : el.scrollLeft + scrollSize;
    }
  }, [direction, scrollSize]);

  useEffect(() => {
    if (isMouseHolding) {
      //if holding mouse, we trigger `repeat`
      scrollItems();
      timerRef.current = setInterval(scrollItems, 400);
    } else {
      clearInterval(timerRef.current);
    }

    return () => clearInterval(timerRef.current);
  }, [isMouseHolding, scrollItems]); //to track mouse holding behaviour changes

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        ...sx
      }}>
      {itemsLength >= minLengthForControls && (
        <Button
          variant={ButtonVariant.secondary}
          aria-label={t("buttons.previous")}
          startIcon={<ArrowBackOutlinedIcon aria-hidden="true" />}
          sx={{
            display: { xs: "none", md: "flex" },
            mr: 3
          }}
          onMouseDown={() => {
            setDirection("left");
            setIsMouseHolding(true);
          }}
          onMouseUp={stop}
        />
      )}

      <Box
        ref={scrollContainerRef}
        sx={() => ({
          display: "flex",
          overflow: "auto",
          overflowY: "hidden",
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none"
          }
        })}>
        {children}
      </Box>

      {itemsLength >= minLengthForControls && (
        <Button
          variant={ButtonVariant.secondary}
          aria-label={t("buttons.next")}
          startIcon={<ArrowForwardOutlinedIcon aria-hidden="true" />}
          sx={{
            display: { xs: "none", md: "flex" },
            ml: 3
          }}
          onMouseDown={() => {
            setDirection("right");
            setIsMouseHolding(true);
          }}
          onMouseUp={stop}
        />
      )}
    </Box>
  );
};

export default SelectionList;
